/**
 * useClarityTracking — Microsoft Clarity helper for React Router SPAs.
 *
 * Clarity's base snippet only fires ONE page view (the initial document load).
 * In a single-page React app every subsequent route change is an internal
 * navigation — Clarity does not see it as a new page. This hook hooks into
 * react-router's useLocation() and notifies Clarity on every route change
 * so each path appears as its own page-view in the dashboard.
 *
 * Additionally:
 *  - identify(userId, traits)  → cross-session user identification so all
 *    sessions from the same user link together (a non-PII hash of email is
 *    used; never raw email).
 *  - tag(key, value)           → arbitrary session tags for filtering, e.g.
 *    user_type=student / plan=all_access / source=meta_ad.
 *
 * Clarity is loaded asynchronously, so window.clarity may not yet be defined
 * at first render — every call here is wrapped in a defensive guard.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// ── Safe wrapper around window.clarity ─────────────────────────────────────
const callClarity = (...args) => {
  if (typeof window === 'undefined') return;
  // window.clarity is a queue-then-replaced function created by the snippet
  if (typeof window.clarity === 'function') {
    try {
      window.clarity(...args);
    } catch (err) {
      // never let analytics crash the app
      // eslint-disable-next-line no-console
      console.debug('[clarity] call failed', err);
    }
  }
};

// ── Identify a user (call once on login) ───────────────────────────────────
// We hash the email so Clarity never receives raw PII (GDPR-friendly).
const sha1Hex = async (str) => {
  if (typeof window === 'undefined' || !window.crypto?.subtle) return str;
  const buf = new TextEncoder().encode(str);
  const hash = await window.crypto.subtle.digest('SHA-1', buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

export const identifyClarityUser = async (email, traits = {}) => {
  if (!email) return;
  const hashed = await sha1Hex(email.toLowerCase().trim());
  // Clarity's identify signature: ('identify', customId, sessionId?, pageId?, friendlyName?)
  callClarity('identify', hashed);
  Object.entries(traits).forEach(([k, v]) => {
    if (v !== undefined && v !== null) callClarity('set', k, String(v));
  });
};

export const tagClaritySession = (key, value) => {
  if (!key || value === undefined || value === null) return;
  callClarity('set', String(key), String(value));
};

// ── Route-change tracker (mount once near the top of the app) ──────────────
export const useClarityTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Tag the current page path so Clarity recordings show a clean breadcrumb.
    // We deliberately strip query-strings to avoid leaking sensitive params
    // (e.g. ?razorpay_order_id=...) into analytics. Path is enough for
    // dashboard navigation.
    callClarity('set', 'page', location.pathname);
  }, [location.pathname]);
};

export default useClarityTracking;
